import { toolSelection } from '../app-connection/types'
import { locale } from '../user/locale'
import { z } from 'zod'
import { paginatedSearchQuery } from '../zod/pagination'
import { status } from './status'
import { candidate } from '../candidate/candidate'
import { company } from '../company/company'
import { interviewCampaignData, promptCampaignData } from './input/store-input'
import { column } from './column'
import { messageTemplate } from './message-template'

const campaignBase = z.object({
    tools: toolSelection.array().default([]),
    id: z.string(),
    title: z.string(),
    locale: locale.default('nl_NL'),
    allowLanguageSwitch: z.boolean().default(false),
    status,
    canStart: z.boolean().default(true),
    formId: z.string().nullable(),
    accountId: z.string().nullable(),
    whatsappBusinessAccountId: z.string().nullable().optional(),
    phoneNumberId: z.string().nullable().optional(),
    customAttributes: z.record(z.string(), z.string()),
    company: company.optional(),
    candidates: z.array(candidate).optional(),
    notificationThreshold: z.number(),
    monthlyConversationLimit: z.number().nullable(),
    startMessageTemplateId: z.string().nullable().optional(),
    website: z
        .object({
            siteId: z.string(),
            siteName: z.string(),
            vacancyUrl: z.string(),
            defaultStartMessageTemplateId: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    recontactSchedule: z
        .array(
            z.object({
                id: z.string().optional(),
                messageTemplateId: z.string(),
                messageTemplate: messageTemplate.nullable().optional(),
                delayMinutes: z.number(),
                sortOrder: z.number(),
            })
        )
        .default([]),
    recontactQuietHoursEnabled: z.boolean().default(false),
    recontactQuietHoursStart: z.string().nullable().default('23:00'),
    recontactQuietHoursEnd: z.string().nullable().default('07:00'),
    startMessageTemplate: messageTemplate.nullable().optional(),
    columns: column.array(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
})

export const interviewCampaign = campaignBase.extend({
    type: z.literal('interview'),
    data: interviewCampaignData,
})

const promptCampaignBase = campaignBase.extend({
    data: promptCampaignData,
})

export const salesCampaign = promptCampaignBase.extend({
    type: z.literal('sales'),
})

export const customCampaign = promptCampaignBase.extend({
    type: z.literal('custom'),
})

export const campaign = z.discriminatedUnion('type', [interviewCampaign, customCampaign, salesCampaign])

export type Campaign = z.infer<typeof campaign>
export type InterviewCampaign = z.infer<typeof interviewCampaign>
export type CustomCampaign = z.infer<typeof customCampaign>
export type SalesCampaign = z.infer<typeof salesCampaign>

export const campaignQuery = paginatedSearchQuery({
    filters: z.object({
        title: z.string().array().optional(),
    }),
    sort: z.enum(['title', '-title', 'created_at', '-created_at']),
})

export type CampaignQuery = z.infer<typeof campaignQuery>
